import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import api from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { useLang } from "@/context/LanguageContext";
import { usePlanActions } from "@/lib/planActions";
import { RefreshCw } from "lucide-react";

const DAYS = ["monday", "tuesday", "wednesday", "thursday", "friday", "saturday", "sunday"];
const slotCategory = (s) => (s.startsWith("snack") ? "snack" : s);

// Replaces an existing generated meal with the chosen recipe (never creates a new meal)
export function ReplaceMealDialog({ open, onClose, recipe, onDone }) {
  const { t, tv } = useLang();
  const actions = usePlanActions();
  const [day, setDay] = useState("monday");
  const [slot, setSlot] = useState(null);
  const { data } = useQuery({ queryKey: ["mealplan"], queryFn: async () => (await api.get("/meal-plan")).data, enabled: open });
  const plan = data?.exists ? data.plan : null;
  const dayObj = plan?.days?.find((d) => d.day === day);
  const cats = recipe?.categories || [recipe?.category];
  const slots = (dayObj?.meals || []).filter((m) => cats.includes(slotCategory(m.slot)));
  const chosen = slot && slots.some((m) => m.slot === slot) ? slot : slots[0]?.slot;

  const confirm = async () => {
    const res = await actions.swap(day, chosen, recipe.id);
    if (res) { onClose(); onDone?.(); }
  };

  if (!recipe) return null;
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="rounded-3xl max-w-md" data-testid="replace-meal-dialog">
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl">{t("replace_meal_title")}</DialogTitle>
          <DialogDescription>{tv(recipe.name)} · {t("replace_meal_sub")} {t("replace_hint")}</DialogDescription>
        </DialogHeader>
        {!plan ? (
          <p className="text-sm text-muted-foreground">{t("no_plan_desc")}</p>
        ) : (
          <div className="space-y-4">
            <div>
              <p className="text-xs uppercase tracking-widest text-muted-foreground font-semibold mb-1.5">{t("choose_day")}</p>
              <div className="flex flex-wrap gap-1.5" data-testid="replace-days">
                {DAYS.map((d) => (
                  <button key={d} onClick={() => setDay(d)} data-testid={`replace-day-${d}`}
                    className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${day === d ? "bg-primary text-primary-foreground border-primary" : "bg-card border-border hover:bg-secondary"}`}>
                    {t(d).slice(0, 3)}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <p className="text-xs uppercase tracking-widest text-muted-foreground font-semibold mb-1.5">{t("choose_slot")}</p>
              <div className="space-y-1.5" data-testid="replace-slots">
                {slots.map((m) => (
                  <button key={m.slot} onClick={() => setSlot(m.slot)} data-testid={`replace-slot-${m.slot}`}
                    className={`w-full flex items-center justify-between rounded-2xl border px-3 py-2 text-left text-sm transition-colors ${chosen === m.slot ? "border-primary bg-chenia-rosesoft/40" : "border-border hover:bg-secondary"}`}>
                    <span className="font-medium">{t(m.slot)}</span>
                    <span className="text-muted-foreground truncate ml-2">{tv(m.name)}</span>
                  </button>
                ))}
              </div>
            </div>
            <Button onClick={confirm} disabled={!chosen || !!actions.busy} className="w-full rounded-full bg-primary hover:bg-chenia-terracottadark" data-testid="replace-confirm">
              {actions.busy ? <RefreshCw className="h-4 w-4 animate-spin" /> : t("replace_now")}
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
